export const USER_ROLES = {
  DEV_TEAM: 'DEV_TEAM',
  SCHOOL_ADMIN: 'SCHOOL_ADMIN',
  TEACHER: 'TEACHER',
  STUDENT: 'STUDENT',
  PARENT: 'PARENT',
  SUPPORT_AGENT: 'SUPPORT_AGENT',
} as const;

export const SCHOOL_STATUS = {
  ACTIVE: 'active',
  INACTIVE: 'inactive',
  SUSPENDED: 'suspended',
} as const;

export const ONBOARDING_STATUS = {
  PENDING: 'pending',
  IN_PROGRESS: 'in_progress',
  COMPLETED: 'completed',
} as const;

export const TOKEN_STORAGE_KEYS = {
  ACCESS_TOKEN: 'access_token',
  REFRESH_TOKEN: 'refresh_token',
} as const;

export const ROUTES = {
  // Landing & marketing
  LANDING: '/',
  LANDING_MS: '/ms',
  LANDING_MALAY: '/malay',
  MODULES: '/modules',
  TEACHER_LANDING: '/teacher',
  PRIVACY: '/privacy',
  PRICING: '/pricing',
  CONTACT: '/contact',
  TERMS: '/terms',
  /** Public MVP: Grade 3 workbook JSON renderer (no API). */
  GRADE3_ASSIGNMENTS_DEMO: '/demo/grade3-assignments',

  // Auth
  LOGIN: '/login',
  REGISTER: '/register',
  FORGOT_PASSWORD: '/forgot-password',
  RESET_PASSWORD: '/reset-password',
  INVITE_VERIFY: '/invite/verify',
  INVITE_ACCEPT: '/invite/accept',
  /** BanyanPro LMS launch bootstrap (public, legacy query-param flow). */
  LMS_LAUNCH: '/lms/launch',
  /** Production LMS authenticate (POST body, no JWT in URL). */
  AUTHENTICATE: '/authenticate',
  /** BanyanPro iframe embed shell (postMessage + optional query fallback). */
  EMBED_BANYANPRO: '/embed/banyanpro',

  // Profile (header dropdown; Coupons for DEV_TEAM)
  PROFILE: '/profile',
  PROFILE_COUPONS: '/profile/coupons',

  // Dev Team
  DEV_DASHBOARD: '/dev/dashboard',
  DEV_SCHOOLS: '/dev/schools',
  DEV_SCHOOLS_CREATE: '/dev/schools/create',
  DEV_SCHOOLS_ONBOARD: '/dev/schools/onboard',
  DEV_SCHOOLS_NEW: '/dev/schools/new',
  DEV_SCHOOL_DETAILS: '/dev/schools/:id',
  DEV_SCHOOL_EDIT: '/dev/schools/:id/edit',
  DEV_AUDIT_LOGS: '/dev/audit-logs',
  /** Centralized Mongo audit (gateway → analytics-service). */
  DEV_AUDIT_CONSOLE: '/dev/audit-console',
  DEV_PLANS: '/dev/plans',
  DEV_PLAN_EDITOR: '/dev/plans/:planId',
  DEV_WEBHOOKS: '/dev/webhooks',
  DEV_BILLING_ANALYTICS: '/dev/billing-analytics',
  DEV_INVITE: '/dev/invite',
  DEV_PROMPT_TEMPLATES: '/dev/prompt-templates',
  DEV_TUTOR_QUOTAS: '/dev/tutor-quotas',
  DEV_SUBSCRIPTIONS: '/dev/subscriptions',
  DEV_SUBSCRIPTION_ALERTS: '/dev/subscription-alerts',
  DEV_ALERTS: '/dev/alerts',
  DEV_COMPLIANCE: '/dev/compliance',
  DEV_BUSINESS: '/dev/business',
  DEV_ANALYTICS: '/dev/analytics',
  DEV_ANALYTICS_STUDENTS: '/dev/analytics/students',
  DEV_STUDENT_TRANSCRIPTS: '/dev/student-transcripts',
  DEV_STUDENT_TRANSCRIPT_DETAIL: '/dev/student-transcripts/:transcriptSessionId',

  // School Admin
  ADMIN_DASHBOARD: '/admin/dashboard',
  ADMIN_SCHOOL: '/admin/school',
  ADMIN_SETUP: '/admin/setup',
  ADMIN_CLASSES: '/admin/classes',
  ADMIN_STUDENTS: '/admin/students',
  ADMIN_STUDENTS_NEW: '/admin/students/new',
  ADMIN_TEACHERS: '/admin/teachers',
  ADMIN_PARENTS: '/admin/parents',
  ADMIN_INVITE_TEACHER: '/admin/invites/teacher',
  ADMIN_INVITE_PARENT: '/admin/invites/parent',
  ADMIN_INVITE_STUDENT: '/admin/invites/student',
  ADMIN_PARENT_LINKS: '/admin/parent-links',
  ADMIN_SECTIONS: '/admin/sections',
  /** Legacy Stripe checkout — redirects to ADMIN_SUBSCRIPTION. */
  ADMIN_BILLING: '/admin/billing',
  ADMIN_SCENARIO_AUTHORING: '/admin/scenario-authoring',
  ADMIN_CONTENT_REVISION_VOCABULARY: '/admin/content/revision-vocabulary',
  ADMIN_BILLING_INVOICES: '/admin/billing/invoices',
  ADMIN_PLANS: '/admin/plans',
  ADMIN_BILLING_HISTORY: '/admin/billing-history',
  ADMIN_SUBSCRIPTION: '/admin/subscription',
  ADMIN_TUTOR_QUOTAS: '/admin/tutor-quotas',
  ADMIN_QUOTA_DASHBOARD: '/admin/quota-dashboard',
  ADMIN_ANALYTICS: '/admin/analytics',
  ADMIN_ANALYTICS_STUDENTS: '/admin/analytics/students',
  ADMIN_ACTIVITY_LOGS: '/admin/activity-logs',
  ADMIN_WEEKLY_REPORTS: '/admin/weekly-reports',
  ADMIN_TUTOR_THEME: '/admin/tutor-theme',
  ADMIN_SUPPORT: '/admin/support',
  ADMIN_SUPPORT_DASHBOARD: '/admin/support/dashboard',
  ADMIN_SUPPORT_TICKETS: '/admin/support/tickets',
  ADMIN_SUPPORT_TICKETS_NEW: '/admin/support/tickets/new',
  ADMIN_SUPPORT_QUEUE: '/admin/support/queue',

  // Teacher
  TEACHER_DASHBOARD: '/teacher/dashboard',
  TEACHER_PROFILE: '/teacher/profile',
  TEACHER_ASSIGNMENTS: '/teacher/assignments',
  /** Opens teacher Assignments → Communication (practice section scrolls into view). */
  TEACHER_ASSIGNMENTS_COMM_TASKS: '/teacher/assignments?open=communication',
  /** Teacher submission review (`:submissionId` = `submission_id`). */
  TEACHER_SUBMISSION_REVIEW: '/teacher/submissions/:submissionId/review',
  TEACHER_ANALYTICS: '/teacher/analytics',
  TEACHER_TUTOR_ANALYTICS: '/teacher/tutor-analytics',
  TEACHER_STUDENT_ANALYTICS: '/teacher/tutor-analytics/student/:studentId',
  TEACHER_CAN_DO: '/teacher/can-do',
  /** Legacy URL; AppRoutes redirects to TEACHER_ASSIGNMENTS_COMM_TASKS. */
  TEACHER_COMMUNICATION_ASSIGNMENTS: '/teacher/communication-assignments',
  TEACHER_COMMUNICATION_ANALYTICS: '/teacher/communication-analytics',
  TEACHER_COMMUNICATION_ALERTS: '/teacher/communication-alerts',
  TEACHER_SCENARIO_CAN_DO: '/teacher/scenario-can-do',
  TEACHER_SCENARIO_AUTHORING: '/teacher/scenario-authoring',
  TEACHER_CONTENT: '/teacher/content',
  TEACHER_CONTENT_LESSONS: '/teacher/content/lessons',
  TEACHER_CONTENT_STORIES: '/teacher/content/stories',
  TEACHER_CONTENT_RHYMES: '/teacher/content/rhymes',
  TEACHER_CONTENT_VOCABULARY: '/teacher/content/vocabulary',
  TEACHER_CONTENT_REVISION_VOCABULARY: '/teacher/content/revision-vocabulary',
  TEACHER_STUDENT_COMMUNICATION_PROGRESS: '/teacher/student-progress', // legacy redirect → class-progress
  TEACHER_CLASS_PROGRESS: '/teacher/class-progress',
  TEACHER_SETTINGS: '/teacher/settings',
  TEACHER_ONBOARDING: '/teacher/onboarding',
  TEACHER_SUPPORT: '/teacher/support',
  TEACHER_SUPPORT_DASHBOARD: '/teacher/support/dashboard',
  TEACHER_SUPPORT_TICKETS: '/teacher/support/tickets',
  TEACHER_SUPPORT_TICKETS_NEW: '/teacher/support/tickets/new',
  
  // Student
  STUDENT_DASHBOARD: '/student/dashboard',
  STUDENT_PROFILE: '/student/profile',
  /** Grade 3 workbook (authenticated); same page component as public demo. */
  STUDENT_ACTIVITY: '/student/activity',
  STUDENT_CONVERSATION: '/student/conversation',
  STUDENT_TAMIL_TUTOR: '/student/ai-tutor-live',
  /** Tamil Sec3 G3 Unit 5 விளக்கக் கட்டுரை — checkpoint writing workspace. */
  STUDENT_TAMIL_COMPOSITION_WRITING: '/student/tamil/composition-writing',
  /** Penulisan Ekspositori — lesson-attached Writing Studio (Unit 5). */
  STUDENT_MALAY_EXPOSITORY_WRITING: '/student/malay/expository-writing',
  STUDENT_COMMUNICATION_TUTOR: '/student/communication-tutor',
  /** Malay Writing Studio unified entry. */
  STUDENT_WRITING_STUDIO: '/student/writing-studio',
  STUDENT_WRITING_STUDIO_ESSAY: '/student/writing-studio/essay',
  STUDENT_WRITING_STUDIO_NARRATIVE: '/student/writing-studio/narrative',
  /** Malay Narrative Writing two-card landing (Phase 2C). Legacy paths redirect to Writing Studio. */
  /** Malay Reading Comprehension (local POC). */
  STUDENT_READING_COMPREHENSION: '/student/reading-comprehension',
  /** Structured DOM textbook page POC demo (parallel to overlay reader). */
  STUDENT_STRUCTURED_READING_DEMO: '/student/demo/structured-reading/:pageId',
  STUDENT_NARRATIVE_WRITING: '/student/narrative-writing',
  STUDENT_NARRATIVE_WRITING_TUTOR: '/student/narrative-writing/tutor',
  STUDENT_NARRATIVE_WRITING_WRITE: '/student/narrative-writing/write',
  /** Deep link for communication practice tab on the unified assignments page. */
  STUDENT_ASSIGNMENTS_COMMUNICATION_TAB: '/student/assignments?tab=communication',
  /** Legacy path; AppRoutes redirects to STUDENT_ASSIGNMENTS_COMMUNICATION_TAB. */
  STUDENT_COMMUNICATION_ASSIGNMENTS: '/student/communication-assignments',
  STUDENT_SCENARIO_CONVERSATION: '/student/scenario-conversation',
  STUDENT_SCENARIO_CLASSROOM: '/student/scenario-conversation/classroom',
  STUDENT_GAMES: '/student/games',
  STUDENT_PHONICS_LAB: '/student/phonics-lab',
  STUDENT_VOCABULARY: '/student/vocabulary',
  STUDENT_VOCABULARY_DICTIONARY: '/student/vocabulary/dictionary',
  STUDENT_VOCABULARY_FLASHCARDS: '/student/vocabulary/flashcards',
  STUDENT_VOCABULARY_REVISION_LESSON: '/student/vocabulary/flashcards/lesson',
  STUDENT_ASSIGNMENTS: '/student/assignments',
  STUDENT_STORY_STUDIO: '/student/story-studio',
  STUDENT_AI_TUTOR_AVATAR: '/student/ai-tutor-avatar',
  STUDENT_PICTURE_TALK: '/student/picture-talk',
  // Full app path including :sceneId — prefer `studentPictureTalkScenePath(id)` for navigation.
  STUDENT_PICTURE_TALK_SCENE: '/student/picture-talk/:sceneId',
  STUDENT_WRITING: '/student/writing',
  STUDENT_WRITING_HISTORY: '/student/writing/history',
  STUDENT_COURSES: '/student/courses',
  STUDENT_COURSE_PLAYER: '/student/courses/:courseId',
  STUDENT_LIBRARY: '/student/library',
  STUDENT_PROGRESS: '/student/progress',
  STUDENT_MALAY_LEARNING_PROGRESS: '/student/malay-learning-progress',
  STUDENT_ANALYTICS: '/student/analytics',
  STUDENT_ANALYTICS_CHARTS: '/student/analytics/charts',
  STUDENT_TUTOR_ANALYTICS: '/student/tutor-analytics',
  STUDENT_SETTINGS: '/student/settings',
  STUDENT_ONBOARDING: '/student/onboarding',
  STUDENT_SUPPORT: '/student/support',
  
  // Parent
  PARENT_DASHBOARD: '/parent/dashboard',
  PARENT_LINK_CHILD: '/parent/link-child',
  PARENT_CHILDREN: '/parent/children',
  PARENT_COMMUNICATION_PROGRESS: '/parent/progress',
  PARENT_SUPPORT: '/parent/support',
  TEACHER_MY_SECTIONS: '/teacher/my-sections',
  
  // Onboarding
  ONBOARDING_SCHOOL_SETUP: '/onboarding/school-setup',
  ONBOARDING_TEACHER_PROFILE: '/onboarding/teacher-profile',
  ONBOARDING_STUDENT_PROFILE: '/onboarding/student-profile',
  
  // Comms Admin
  ADMIN_COMMS_EMAIL_CONFIGS: '/admin/comms/email-configs',
  ADMIN_COMMS_EMAIL_TEMPLATES: '/admin/comms/email-templates',
  // Dev comms (same pages exposed under /dev)
  DEV_COMMS_EMAIL_CONFIGS: '/dev/comms/email-configs',
  DEV_COMMS_EMAIL_TEMPLATES: '/dev/comms/email-templates',
  DEV_WEEKLY_REPORTS: '/dev/weekly-reports',
  DEV_SUPPORT: '/dev/support',
  DEV_SUPPORT_DASHBOARD: '/dev/support/dashboard',
  DEV_SUPPORT_TICKETS: '/dev/support/tickets',
  DEV_SUPPORT_QUEUE: '/dev/support/queue',
  /** Dev-only structured page source vs render compare tool. */
  DEV_DIGITAL_PAGE_COMPARE: '/dev/digital-page-compare/:pageId',
  /** Shortcut — pageId contains `/` so use this URL in the browser. */
  DEV_DIGITAL_PAGE_COMPARE_JENDELA_P39: '/dev/digital-page-compare/jendela-p39',
  /** DEV_TEAM interactive book authoring studio home. */
  DEV_AUTHORING_HOME: '/dev/structured-textbook',
  /** DEV_TEAM structured textbook book library. */
  DEV_AUTHORING_BOOKS: '/dev/structured-textbook/books',
  DEV_AUTHORING_BOOK: '/dev/structured-textbook/books/:bookId',
  DEV_AUTHORING_SOURCE_PAGE: '/dev/structured-textbook/books/:bookId/source/:sourcePageId',
  DEV_AUTHORING_PAGE_EDIT: '/dev/structured-textbook/books/:bookId/pages/:pageId/edit',
  DEV_AUTHORING_PAGE_PREVIEW: '/dev/structured-textbook/books/:bookId/pages/:pageId/preview',
  /** DEV_TEAM migration v2 review console (dry-run only in Phase 3B1). */
  DEV_AUTHORING_MIGRATION: '/dev/structured-textbook/migration',
  /** DEV_TEAM exact-page interactive book QA list (non-authoring). */
  DEV_INTERACTIVE_BOOKS: '/dev/interactive-books',
  /** DEV_TEAM exact-page interactive book QA preview (non-authoring). */
  DEV_INTERACTIVE_BOOK_PREVIEW: '/dev/interactive-books/:bookId/preview',
  DEV_INTERACTIVE_BOOK_PREVIEW_PAGE: '/dev/interactive-books/:bookId/preview/:pageIndex',
  /** DEV_TEAM interactive book composer (multi-PDF import). */
  DEV_INTERACTIVE_BOOK_COMPOSER: '/dev/interactive-books/:bookId/composer',
  /** Tamil Keeladi exact-page learner route. */
  STUDENT_TAMIL_KEELADI_READING: '/student/tamil/keeladi-reading',
  STUDENT_TAMIL_KEELADI_READING_PAGE: '/student/tamil/keeladi-reading/:pageIndex',
  /** Teammate iBook publish/student distribution (adapted alongside canonical interactive-books). */
  DEV_IBOOK_LIST: '/dev/ibook',
  DEV_IBOOK_NEW: '/dev/ibook/new',
  DEV_IBOOK_EDIT: '/dev/ibook/:bookId/edit',
  DEV_IBOOK_PREVIEW: '/dev/ibook/:bookId/preview',
  DEV_IBOOK_PUBLISH: '/dev/ibook/:bookId/publish',
  DEV_TENANT_IBOOKS: '/dev/schools/:tenantId/ibooks',
  /** Legacy BookFlow path redirect */
  DEV_BOOKFLOW: '/dev/bookflow',
  /** Student published interactive books library */
  STUDENT_IBOOKS: '/student/ibooks',
  STUDENT_IBOOK_READER: '/student/ibooks/:bookId',
  STUDENT_INTERACTIVE_BOOK_READER: '/student/ibooks/interactive/:bookId',
} as const;

export function devAuthoringBookPath(bookId: string): string {
  return `/dev/structured-textbook/books/${encodeURIComponent(bookId)}`;
}

export function devAuthoringSourcePagePath(bookId: string, sourcePageId: string): string {
  return `/dev/structured-textbook/books/${encodeURIComponent(bookId)}/source/${encodeURIComponent(sourcePageId)}`;
}

export function devAuthoringEditPath(bookId: string, pageId: string): string {
  return `/dev/structured-textbook/books/${encodeURIComponent(bookId)}/pages/${encodeURIComponent(pageId)}/edit`;
}

export function devAuthoringPreviewPath(bookId: string, pageId: string): string {
  return `/dev/structured-textbook/books/${encodeURIComponent(bookId)}/pages/${encodeURIComponent(pageId)}/preview`;
}

export function devInteractiveBookPreviewPath(bookId: string, pageIndex?: number): string {
  const base = `/dev/interactive-books/${encodeURIComponent(bookId)}/preview`;
  if (pageIndex == null || pageIndex <= 0) {
    return base;
  }
  return `${base}/${pageIndex}`;
}

export function devInteractiveBookComposerPath(bookId: string): string {
  return `/dev/interactive-books/${encodeURIComponent(bookId)}/composer`;
}

export function studentKeeladiInteractiveBookPath(pageIndex?: number): string {
  if (pageIndex == null || pageIndex <= 0) {
    return '/student/tamil/keeladi-reading';
  }
  return `/student/tamil/keeladi-reading/${pageIndex}`;
}

/** Student Picture Talk detail URL (`/student/picture-talk/:sceneId`). */
export function studentPictureTalkScenePath(sceneId: string): string {
  return `${ROUTES.STUDENT_PICTURE_TALK}/${encodeURIComponent(sceneId)}`;
}

/** Teacher submission review URL (`/teacher/submissions/:submissionId/review`). */
export function teacherSubmissionReviewPath(submissionId: string): string {
  return `/teacher/submissions/${encodeURIComponent(submissionId)}/review`;
}

